import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons"; // Import icons
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { LuNewspaper, LuStethoscope } from "react-icons/lu";
import { MdOutlineMiscellaneousServices } from "react-icons/md";
import { LuUniversity } from "react-icons/lu";
import {
  FaUserFriends,
  FaUniversity,
  FaInfoCircle,
  FaEnvelope,
  FaVideo,
} from "react-icons/fa"; // Import icons for options and Webinar
import Cookies from "js-cookie"; // Import js-cookie
import Animation from "../public/Animation/Animation.json"; // Import Animation component.
import { IoCartOutline } from "react-icons/io5";
import { useCart } from "../context/CartContext"; // Import useCart
import CartDrawer from "./CartDrawer.js"; // Import CartDrawer component
import { searchGhostPosts } from "../utils/ghostSearch";
import Head from "next/head";
const Player = dynamic(
  () => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player),
  { ssr: false }
);

// Safe function to set cookies with proper options
const setSafeCookie = (name, value, options = {}) => {
  const isProd = process.env.NODE_ENV === "production";
  const defaultOptions = {
    path: "/",
    secure: isProd,
    sameSite: isProd ? "None" : "Lax",
    expires: 1, // 1 day
  };
  Cookies.set(name, value, { ...defaultOptions, ...options });
};

// Safe function to remove cookies
const removeSafeCookie = (name) => {
  const isProd = process.env.NODE_ENV === "production";
  const domain = isProd ? ".neetsupport.com" : undefined;
  Cookies.remove(name, {
    path: "/",
    domain,
    sameSite: isProd ? "None" : "Lax",
  });
};

// Safe function to get cookies only on the client-side
// "NEW" blink on the Rank Predictor menu for the NEET PG 2026 result launch.
// It turns itself off after this moment, so no follow-up deploy is needed.
const RANK_PREDICTOR_NEW_BADGE_UNTIL = new Date("2026-09-28T00:00:00+05:30");

const NewBadge = () => (
  <span className="nav-new-badge ml-1.5 rounded-full bg-[#ff4d4f] px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
    New
  </span>
);

const getSafeCookie = (name) => {
  const isProd = process.env.NODE_ENV === "production";
  const domain = isProd ? ".neetsupport.com" : undefined;
  return Cookies.get(name, { domain }) || null;
};

export default function Header() {
  const { cartItems } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false); // State to manage sidebar visibility
  const [isLoggedIn, setIsLoggedIn] = useState(false); // User login state
  const [isCartOpen, setIsCartOpen] = useState(false); // State to manage cart drawer visibility
  const [posts, setPosts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const authToken = getSafeCookie("authToken"); // Get authToken safely
  const router = useRouter();
  const currentPath = router.pathname;
  const showHeaderSearch = currentPath !== "/";
  // Only used for animation, not for logo image
  const [isClient, setIsClient] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false); // Modal open/close state
  const [searchQuery, setSearchQuery] = useState(""); // Store search input value
  const [showSidebarMore, setShowSidebarMore] = useState(false); // For sidebar "More" dropdown
  const [showDesktopMore, setShowDesktopMore] = useState(false); // Add this state for desktop More dropdown
  const [showWebinarDropdown, setShowWebinarDropdown] = useState(false);
  const [showWebinarMore, setShowWebinarMore] = useState(false);

  // NEW: dropdown state for News & Blogs
  const [showNewsDropdown, setShowNewsDropdown] = useState(false); // Desktop News & Blogs
  const [showSidebarNews, setShowSidebarNews] = useState(false); // Mobile News & Blogs
  const [showRankDropdown, setShowRankDropdown] = useState(false); // Desktop Rank Predictor
  const [showSidebarRank, setShowSidebarRank] = useState(false); // Mobile Rank Predictor
  const [showRankNewBadge, setShowRankNewBadge] = useState(false);

  // Checked on the client so server-rendered HTML never disagrees with the date.
  useEffect(() => {
    setShowRankNewBadge(Date.now() < RANK_PREDICTOR_NEW_BADGE_UNTIL.getTime());
  }, []);
  const [showMbbsDropdown, setShowMbbsDropdown] = useState(false); // Desktop MBBS Abroad dropdown
  const [showSidebarMbbs, setShowSidebarMbbs] = useState(false); // Mobile MBBS Abroad dropdown

  // Dropdown toggle handlers to ensure only one is open at a time
  const handleNewsDropdown = () => {
    setShowNewsDropdown((prev) => {
      const next = !prev;
      if (next) {
        setShowWebinarDropdown(false);
        setShowDesktopMore(false);
      }
      return next;
    });
  };
  const handleRankDropdown = () => {
    setShowRankDropdown((prev) => {
      const next = !prev;
      if (next) {
        setShowNewsDropdown(false);
        setShowWebinarDropdown(false);
        setShowDesktopMore(false);
      }
      return next;
    });
  };
  const handleWebinarDropdown = () => {
    setShowWebinarDropdown((prev) => {
      const next = !prev;
      if (next) {
        setShowNewsDropdown(false);
        setShowDesktopMore(false);
      }
      return next;
    });
  };
  const handleDesktopMoreDropdown = () => {
    setShowDesktopMore((prev) => {
      const next = !prev;
      if (next) {
        setShowNewsDropdown(false);
        setShowWebinarDropdown(false);
      }
      return next;
    });
  };
  // Mobile sidebar dropdowns
  const handleSidebarNews = () => {
    setShowSidebarNews((prev) => {
      const next = !prev;
      if (next) {
        setShowSidebarMore(false);
        setShowWebinarMore(false);
      }
      return next;
    });
  };
  const handleSidebarRank = () => {
    setShowSidebarRank((prev) => {
      const next = !prev;
      if (next) {
        setShowSidebarNews(false);
        setShowWebinarMore(false);
        setShowSidebarMore(false);
      }
      return next;
    });
  };
  const handleSidebarWebinarMore = () => {
    setShowWebinarMore((prev) => {
      const next = !prev;
      if (next) {
        setShowSidebarNews(false);
        setShowSidebarMore(false);
      }
      return next;
    });
  };
  const handleSidebarMbbs = () => {
    setShowSidebarMbbs((prev) => {
      const next = !prev;
      if (next) {
        setShowSidebarNews(false);
        setShowSidebarMore(false);
        setShowWebinarMore(false);
      }
      return next;
    });
  };
  const handleSidebarMore = () => {
    setShowSidebarMore((prev) => {
      const next = !prev;
      if (next) {
        setShowSidebarNews(false);
        setShowWebinarMore(false);
      }
      return next;
    });
  };

  // Mapping of tags to icons with fixed size
  const tagIconMap = {
    college: <FaUniversity className="mr-3 text-[#ff9245] text-lg" />, // Fixed size
    counselling: <FaUserFriends className="mr-3 text-[#ff9245] text-lg" />, // Fixed size
    blogs: <LuNewspaper className="mr-3 text-[#ff9245] text-lg" />, // Fixed size
  };

  // MBBS abroad countries with flags
  const mbbsCountries = [
    { name: "China", slug: "china", flag: "🇨🇳", flagCode: "cn" },
    { name: "Vietnam", slug: "vietnam", flag: "🇻🇳", flagCode: "vn" },
    { name: "Philippines", slug: "philippines", flag: "🇵🇭", flagCode: "ph" },
    { name: "New Zealand", slug: "new-zealand", flag: "🇳🇿", flagCode: "nz" },
    { name: "Bangladesh", slug: "bangladesh", flag: "🇧🇩", flagCode: "bd" },
    { name: "Kazakhstan", slug: "kazakhstan", flag: "🇰🇿", flagCode: "kz" },
    { name: "Kyrgyzstan", slug: "kyrgyzstan", flag: "🇰🇬", flagCode: "kg" },
    { name: "United States", slug: "usa", flag: "🇺🇸", flagCode: "us" },
    { name: "United Kingdom", slug: "uk", flag: "🇬🇧", flagCode: "gb" },
    { name: "UAE", slug: "uae", flag: "🇦🇪", flagCode: "ae" },
    { name: "Luxembourg", slug: "luxembourg", flag: "🇱🇺", flagCode: "lu" },
    { name: "Japan", slug: "japan", flag: "🇯🇵", flagCode: "jp" },
    { name: "Germany", slug: "germany", flag: "🇩🇪", flagCode: "de" },
    { name: "Netherlands", slug: "netherlands", flag: "🇳🇱", flagCode: "nl" },
    { name: "Ireland", slug: "ireland", flag: "🇮🇪", flagCode: "ie" },
    { name: "Belarus", slug: "belarus", flag: "🇧🇾", flagCode: "by" },
    { name: "Australia", slug: "australia", flag: "🇦🇺", flagCode: "au" },
    { name: "Canada", slug: "canada", flag: "🇨🇦", flagCode: "ca" },
    { name: "Russia", slug: "russia", flag: "🇷🇺", flagCode: "ru" },
    { name: "Georgia", slug: "georgia", flag: "🇬🇪", flagCode: "ge" },
    { name: "Uzbekistan", slug: "uzbekistan", flag: "🇺🇿", flagCode: "uz" },
    { name: "Malaysia", slug: "malaysia", flag: "🇲🇾", flagCode: "my" },
    { name: "Nepal", slug: "nepal", flag: "🇳🇵", flagCode: "np" },
  ];
  const sortedMbbsCountries = [...mbbsCountries].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const FlagIcon = ({ country, className = "" }) => {
    const [imageError, setImageError] = useState(false);

    if (imageError || !country.flagCode) {
      return (
        <span
          className={`text-sm sm:text-lg flex-shrink-0 leading-none ${className}`}
          style={{
            fontFamily:
              '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif',
          }}
        >
          {country.flag}
        </span>
      );
    }

    return (
      <img
        src={`https://flagcdn.com/w40/${country.flagCode}.png`}
        alt={`${country.name} flag`}
        loading="lazy"
        onError={() => setImageError(true)}
        className={`h-4 w-6 rounded-sm object-cover flex-shrink-0 ${className}`}
      />
    );
  };
  // Function to verify token
  const verifyToken = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/verifyToken`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Token verification failed");
      }

      return response.status === 200;
    } catch (error) {
      console.error("Error verifying token:", error);
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleInputFocus = async () => {
    // Do not eagerly fetch all posts on focus — show dropdown and wait for typed query
    setShowResults(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowResults(false), 200); // Hide search results after a delay to allow click events
  };

  const handleSearch = (e) => {
    const raw = (e && e.target && e.target.value) || "";
    const value = raw.toString();
    setSearchQuery(value);

    // debounce filtering to avoid running on every keystroke
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      const q = value.trim().toLowerCase();
      if (!q) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      // Query server-side search API which performs a filtered, paginated query
      try {
        setIsLoadingResults(true);
        const postsResp = await searchGhostPosts(q);
        setSearchResults(postsResp);
      } catch (err) {
        console.error('Error searching posts via Ghost API:', err);
        setSearchResults([]);
      } finally {
        setIsLoadingResults(false);
      }
      setShowResults(true);
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const getPostUrl = (post) => {
    const matchingTag = post.tags?.find((tag) => {
      const value = (tag?.name || tag?.slug || "").toString().toLowerCase();
      return ['college', 'counselling', 'blogs'].includes(value);
    });

    const tagName = (matchingTag?.name || matchingTag?.slug || "").toString().toLowerCase();

    if (tagName === 'college') return `/college/${post.slug}`;
    if (tagName === 'counselling') return `/counselling/${post.slug}`;
    if (tagName === 'blogs') return `/blogs/${post.slug}`;

    return post.canonical_url || post.url || `/blogs/${post.slug}`;
  };

  const handleResultClick = (post) => {
    setShowResults(false);
    router.push(getPostUrl(post));
  };
  const handleCloseSearch = () => {
    setIsSearchOpen(false); // Close the search modal
    setShowResults(false); // Hide the results when modal is closed
  };
  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowResults(false);
    }
  };

  const handleInputClick = () => {
    if (inputRef.current.value) {
      setShowResults(true);
    }
  };

  // Check token validity on component mount
  useEffect(() => {
    const checkLoginStatus = async () => {
      if (authToken) {
        const validToken = await verifyToken(authToken);
        setIsLoggedIn(validToken); // Update login state
      } else {
        // Clear cookies if authToken is invalid or missing
        document.cookie.split(";").forEach((c) => {
          const cookieName = c.split("=")[0].trim();
          removeSafeCookie(cookieName);
        });
      }
    };
    checkLoginStatus();
  }, [authToken]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Only needed for animation, not for logo image
  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen); // Toggle the sidebar menu
    if (!isMenuOpen) {
      setIsSearchOpen(false); // Close the search bar if opening the sidebar
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false); // Close the sidebar menu
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === "sidebar-overlay") {
      closeMenu();
    }
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen); // Toggle the cart drawer
  };

  // Helper to close all dropdowns
  const closeAllDropdowns = () => {
    setShowNewsDropdown(false);
    setShowWebinarDropdown(false);
    setShowDesktopMore(false);
    setShowMbbsDropdown(false);
    setShowRankDropdown(false);

    setShowSidebarNews(false);
    setShowWebinarMore(false);
    setShowSidebarMore(false);
    setShowSidebarMbbs(false);
    setShowSidebarRank(false);
  };
  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href="/favicon/neetsupport-logo.webp"
          type="image/webp"
        />
      </Head>
      <header className=" fixed top-0 left-0 w-full transition-transform duration-300 z-50 bg-white shadow-md">
        <div className="flex items-center justify-between p-3 text-gray-500">
          {/* Mobile Menu Icon */}
          <div className="xl:hidden flex items-center justify-between w-full">
            {/* Menu Toggle Button and Logo Section */}
            <div className="flex items-center">
              <button
                onClick={toggleMenu}
                className="text-[#ff9245] focus:outline-none mr-1"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                <FontAwesomeIcon
                  icon={isMenuOpen ? faTimes : faBars}
                  className="h-6 w-6"
                />
              </button>

              {/* Logo Section: Always rendered server-side for LCP */}
              <Link href="/" className="flex items-center">
                <img
                  src="/favicon/neetsupport-logo.webp"
                  alt="Neet Support"
                  width={200}
                  height={50}
                  fetchpriority="high"
                  className="h-auto w-auto"
                />
              </Link>
            </div>

            {/* Login or Dashboard Button */}
            {isLoggedIn ? (
              <Link
                href="/dashboard"
                className="px-4 sm:px-10 py-2 rounded-full bg-[#ff9245] text-sm sm:text-base text-white border border-[#ff9245] 
                      hover:bg-white hover:!text-[#ff9245] transition-colors duration-300"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                href="/login"
                className="px-4 py-2 rounded-full border border-[#ff9245] text-[#ff9245] text-sm sm:text-base hover:bg-[#ff9245] hover:text-white transition-colors duration-300"
              >
                Log In
              </Link>
            )}
          </div>

          {/* Logo Section for Desktop */}
          <div className="hidden xl:flex items-center">
            {/* Logo Section for Desktop: Always rendered server-side for LCP */}
            <Link href="/" className="flex items-center">
              <img
                src="/favicon/neetsupport-logo.webp"
                alt="Neet Support"
                width={200}
                height={50}
                fetchpriority="high"
                className="h-auto w-auto"
              />
            </Link>
          </div>

          {/* Navigation Links for Desktop */}
          <nav
            className="hidden xl:flex items-center gap-4 2xl:gap-6 font-medium mx-4 whitespace-nowrap"
            style={{ color: "#5C5C5C" }}
          >
            <Link
              href="/neet-ug"
              className="order-1 hover:text-[#ff9245] text-[15px] whitespace-nowrap"
            >
              <b>NEET UG</b>
            </Link>
            <div
              className="order-3 relative"
              onMouseEnter={() => setShowMbbsDropdown(true)}
              onMouseLeave={() => setShowMbbsDropdown(false)}
            >
              <button
                className="hover:text-[#ff9245] text-[15px] flex items-center gap-1 focus:outline-none transition-colors duration-200"
                type="button"
                aria-haspopup="true"
                aria-expanded={showMbbsDropdown}
                onClick={() => {
                  router.push("/mbbs-abroad");
                }}
              >
                <b>MBBS Abroad</b>
                <svg
                  className={`w-3 h-3 ml-1 transition-transform duration-200 ${showMbbsDropdown ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showMbbsDropdown && (
                <div className="absolute left-0 top-full pt-2 w-[560px] max-w-[90vw] z-50">
                  <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {sortedMbbsCountries.map((country) => (
                        <Link
                          key={country.slug}
                          href={`/mbbs-in-${country.slug}`}
                          className="px-3 py-2 rounded-md text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-sm flex items-center justify-between"
                          onClick={() => setShowMbbsDropdown(false)}
                          aria-label={`MBBS in ${country.name}`}
                        >
                          <span className="flex items-center gap-2 truncate">
                            <FlagIcon country={country} />
                            <span className="text-xs sm:text-sm truncate">
                              {country.name}
                            </span>
                          </span>

                          <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link
              href="/neet-pg"
              className="order-2 hover:text-[#ff9245] text-[15px] whitespace-nowrap"
            >
              <b>NEET PG</b>
            </Link>

            {/* NEW: News & Blogs dropdown (replaces Blogs / Counselling / Colleges) */}
            <div className="order-7 relative">
              <button
                className="hover:text-[#ff9245] text-[15px] flex items-center gap-1 focus:outline-none transition-colors duration-200"
                type="button"
                aria-haspopup="true"
                aria-expanded={showNewsDropdown}
                onClick={handleNewsDropdown}
              >
                <b>News &amp; Blogs</b>
                <svg
                  className={`w-3 h-3 ml-1 transition-transform duration-200 ${showNewsDropdown ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showNewsDropdown && (
                <div
                  className="absolute left-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
                  style={{ transition: "opacity 0.2s" }}
                  onMouseLeave={() => setShowNewsDropdown(false)}
                >
                  <Link
                    href="/blogs"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-t-xl font-medium transition-colors duration-150"
                    onClick={() => setShowNewsDropdown(false)}
                  >
                    <LuNewspaper className="w-5 h-5 text-[#ff9245] flex-shrink-0" />
                    <span>Blogs</span>
                  </Link>

                  <Link
                    href="/counselling"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] font-medium transition-colors duration-150"
                    onClick={() => setShowNewsDropdown(false)}
                  >
                    <FaUserFriends className="w-5 h-5 text-[#ff9245] flex-shrink-0" />
                    <span>Counselling</span>
                  </Link>

                  <Link
                    href="/collegeList"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-b-xl font-medium transition-colors duration-150"
                    onClick={() => setShowNewsDropdown(false)}
                  >
                    <FaUniversity className="w-5 h-5 text-[#ff9245] flex-shrink-0" />
                    <span>Colleges</span>
                  </Link>
                </div>
              )}
            </div>

            <Link
              href="/services"
              className="order-6 hover:text-[#ff9245] text-[15px]"
              onClick={closeAllDropdowns}
            >
              <b>Services</b>
            </Link>
            <div className="order-4 relative">
              <button
                className="flex items-center gap-1 text-[15px] transition-colors duration-200 hover:text-[#ff9245] focus:outline-none"
                type="button"
                aria-haspopup="true"
                aria-expanded={showRankDropdown}
                onClick={handleRankDropdown}
              >
                <b>Rank Predictor</b>
                {showRankNewBadge && <NewBadge />}
                <svg className={`ml-1 h-3 w-3 transition-transform duration-200 ${showRankDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
                </svg>
              </button>
              {showRankDropdown && (
                <div className="absolute left-0 mt-2 w-56 rounded-xl border border-gray-200 bg-white shadow-lg z-50" onMouseLeave={() => setShowRankDropdown(false)}>
                  <Link href="/neet-rank-predictor" className="flex items-center gap-3 rounded-t-xl px-4 py-3 text-[15px] font-medium text-gray-700 transition-colors duration-150 hover:bg-orange-50 hover:text-[#ff9245]" onClick={() => setShowRankDropdown(false)}>
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-orange-50 text-sm font-bold text-[#ff9245]">UG</span>
                    <span>NEET UG</span>
                  </Link>
                  <Link href="/neet-pg-rank-predictor" className="flex items-center gap-3 rounded-b-xl px-4 py-3 text-[15px] font-medium text-gray-700 transition-colors duration-150 hover:bg-orange-50 hover:text-[#ff9245]" onClick={() => setShowRankDropdown(false)}>
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-teal-50 text-sm font-bold text-[#097f78]">PG</span>
                    <span>NEET PG</span>
                    {showRankNewBadge && <NewBadge />}
                  </Link>
                </div>
              )}
            </div>
            <Link
              href="/neet-college-predictor"
              className="order-5 hover:text-[#ff9245] text-[15px]"
              onClick={closeAllDropdowns}
            >
              <b>College Predictor</b>
            </Link>

            <div className="order-8 relative">
              <button
                className="hover:text-[#ff9245] text-[15px] flex items-center gap-1 focus:outline-none transition-colors duration-200"
                type="button"
                aria-haspopup="true"
                aria-expanded={showWebinarDropdown}
                onClick={handleWebinarDropdown}
              >
                <b>Webinar</b>
                {/* Small arrow */}
                <svg
                  className={`w-3 h-3 ml-1 transition-transform duration-200 ${showWebinarDropdown ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showWebinarDropdown && (
                <div
                  className="absolute left-0 mt-2 w-28 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
                  style={{ transition: "opacity 0.2s" }}
                  onMouseLeave={() => setShowWebinarDropdown(false)}
                >
                  <Link
                    href="/ugwebinar"
                    className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-t-xl transition-colors duration-150"
                    onClick={() => setShowWebinarDropdown(false)}
                  >
                    NEET UG
                  </Link>

                  <button
                    className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-b-xl transition-colors duration-150"
                    onClick={() => {
                      window.open(
                        "https://livepgwebinar.neetsupport.com/",
                        "_blank"
                      );
                      setShowWebinarDropdown(false);
                    }}
                  >
                    NEET PG
                  </button>
                </div>
              )}
            </div>

            <div className="order-9 relative">
              <button
                className={`hover:text-[#ff9245] text-[15px] flex items-center gap-1 focus:outline-none transition-colors duration-200`}
                type="button"
                aria-haspopup="true"
                aria-expanded={showDesktopMore}
                tabIndex={0}
                onClick={handleDesktopMoreDropdown}
              >
                <b>More</b>
                <svg
                  className={`w-3 h-3 ml-1 transition-transform duration-200 ${showDesktopMore ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showDesktopMore && (
                <div
                  className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
                  style={{ transition: "opacity 0.2s" }}
                  onMouseLeave={() => setShowDesktopMore(false)}
                >
                  <Link
                    href="/About-Us"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-xl font-medium transition-colors duration-150"
                    onClick={() => setShowDesktopMore(false)}
                  >
                    <FaInfoCircle className="w-5 h-5 text-[#ff9245] flex-shrink-0" />
                    <span>About Us</span>
                  </Link>

                  <Link
                    href="/contact"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-xl font-medium transition-colors duration-150"
                    onClick={() => setShowDesktopMore(false)}
                  >
                    <FaEnvelope className="w-5 h-5 text-[#ff9245] flex-shrink-0" />
                    <span>Contact</span>
                  </Link>

                  <Link
                    href="/book-1-on-1-neet-counselling-session"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-xl font-medium transition-colors duration-150"
                    onClick={closeMenu}
                  >
                    <FaUserFriends className="w-5 h-5  text-[#ff9245] flex-shrink-0" />
                    <span> 1:1 Session</span>
                  </Link>
                  <Link
                    href="/seminar"
                    className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-[#ff9245] text-[15px] rounded-xl font-medium transition-colors duration-150"
                    onClick={() => setShowDesktopMore(false)}
                  >
                    <FaVideo className="w-5 h-5 text-[#ff9245] flex-shrink-0" />
                    <span>Seminar</span>
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* Search Bar for Desktop */}
          {showHeaderSearch && (
            <div className="hidden 2xl:block relative">
              <div className="relative">
                <div className="hidden 2xl:block relative">
                  <div className="relative">
                    {/* Hide the input and icon when search modal is open */}
                    {!isSearchOpen && (
                      <>
                        <input
                          type="text"
                          value={searchQuery}
                          placeholder="Enter Keywords, Topics, Questions & more"
                          className="rounded-3xl border-2 border-[#ff9245] w-[300px] px-2 h-[40px] pr-10 placeholder:text-[13px] focus:outline-none focus:ring-1 focus:ring-[#ff9245] focus:border-[#ff9245]"
                          onFocus={() => {
                            setIsSearchOpen(true); // Open the search modal on focus
                          }}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            handleSearch(e);
                          }}
                          onClick={() => {
                            if (searchQuery) setShowResults(true);
                          }}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#ff9245]"
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Login / Signup Buttons OR Dashboard Button */}
          <div className="hidden 2xl:flex space-x-4 text-[15px] login-signup">
            {isLoggedIn ? (
              <Link
                href="/dashboard"
                className="px-10 py-2 rounded-full bg-[#ff9245] text-white border border-[#ff9245] 
                      hover:bg-white hover:!text-[#ff9245] transition-colors duration-300"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  className="px-9 py-2 rounded-full border border-[#ff9245] text-[#F58C42] hover:bg-[#ff9245] hover:text-white transition-colors duration-300 text-[15px]"
                >
                  Log In
                </Link>
                <Link
                  href="/signup"
                  className="group px-9 py-2 rounded-full bg-[#ff9245] text-white border border-[#ff9245] 
             hover:bg-white transition-colors duration-300"
                >
                  <span className="group-hover:text-[#ff9245] transition-colors duration-300">
                    Sign Up
                  </span>
                </Link>
              </>
            )}
          </div>
          {/* Only Log In for xl and below */}
          <div className="hidden xl:flex 2xl:hidden space-x-4 text-[15px] login-signup">
            {isLoggedIn ? (
              <Link
                href="/dashboard"
                className="px-10 py-2 rounded-full bg-[#ff9245] text-white border border-[#ff9245] 
                      hover:bg-white hover:!text-[#ff9245] transition-colors duration-300"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                href="/login"
                className="px-9 py-2 rounded-full border border-[#ff9245] text-[#ff9245] hover:bg-[#ff9245] hover:text-white transition-colors duration-300 text-[15px]"
              >
                Log In
              </Link>
            )}
          </div>
        </div>
        {/* Sidebar Menu for Mobile */}
        {isMenuOpen && (
          <div
            id="sidebar-overlay"
            className="fixed inset-0 bg-gray-400 bg-opacity-50  mt-[82px] z-40"
            onClick={handleOverlayClick}
          >
            <div
              className={`absolute left-0 h-[calc(100vh-82px)] overflow-y-auto bg-white z-50 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
              <div className="p-4 mt-2">
                {/* Navigation Links for Sidebar */}
                {showHeaderSearch && <div className="relative search-bar">
                  <div className="flex-1 ">
                    <input
                      type="text"
                      value={searchQuery}
                      placeholder="Enter Keywords, Topics..."
                      className="px-3 py-2 pr-10 rounded-full border border-[#ff9245] focus:outline-none focus:ring-1 focus:ring-[#ff9245] w-full placeholder:text-xs"
                      onFocus={() => {
                        setIsSearchOpen(true); // Open search modal on focus
                        closeMenu(); // Close the sidebar
                      }}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        handleSearch(e);
                      }}
                    />
                  </div>
                  <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#ff9245]">
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      className="h-4 w-4"
                    />
                  </button>
                  {showResults && searchResults.length > 0 && (
                    <ul className="absolute top-full left-0 w-full bg-white border border-gray-300 mt-1 z-10 max-h-60 overflow-y-auto rounded-lg shadow-lg text-sm">
                      {searchResults.map((post) => (
                        <li
                          key={post.id}
                          className="p-2 hover:bg-orange-100 cursor-pointer"
                          onClick={() => {
                            handleResultClick(post);
                            setSearchQuery(""); // Clear the search query
                          }}
                        >
                          {post.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>}
                <nav className="flex flex-col space-y-6 ml-[24px] mt-8">
                  <Link
                    href="/neet-ug"
                    className="order-1 flex items-center text-gray-800 hover:text-[#ff9245]"
                    onClick={closeMenu}
                  >
                    <LuStethoscope className="mr-3 text-[#ff9245] text-xl" />
                    NEET UG
                  </Link>
                  <div className="order-3 relative">
                    <div className="flex items-center gap-3 w-full">
                      <button
                        type="button"
                        onClick={handleSidebarMbbs}
                        aria-haspopup="true"
                        aria-expanded={showSidebarMbbs}
                        className="p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
                      >
                        <svg
                          className={`w-5 h-5 transition-transform ${showSidebarMbbs ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="#ff9245"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      <Link
                        href="/mbbs-abroad"
                        className="flex items-center text-gray-800 hover:text-[#ff9245]"
                        onClick={closeMenu}
                      >

                        MBBS Abroad
                      </Link>
                    </div>

                    {showSidebarMbbs && (
                      <div className="ml-0 mt-3 pr-2">
                        <div className="grid grid-cols-2 gap-2 max-h-[56vh] overflow-y-auto">
                          {sortedMbbsCountries.map((country) => (
                            <Link
                              key={country.slug}
                              href={`/mbbs-in-${country.slug}`}
                              className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 bg-white hover:bg-orange-50"
                              onClick={closeMenu}
                              aria-label={`MBBS in ${country.name}`}
                            >
                              <div className="flex items-center gap-2">
                                <FlagIcon country={country} />
                                <span className="text-xs sm:text-sm truncate">{country.name}</span>
                              </div>
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center">
                          <Link
                            href="/mbbs-abroad"
                            className="ml-2 flex items-center text-[#0b5cff] font-medium"
                            onClick={closeMenu}
                          >
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                  <Link
                    href="/neet-pg"
                    className="order-2 flex items-center text-gray-800 hover:text-[#ff9245]"
                    onClick={closeMenu}
                  >
                    <LuStethoscope className="mr-3 text-[#ff9245] text-xl" />
                    NEET PG
                  </Link>

                  {/* NEW: News & Blogs dropdown for mobile sidebar */}
                  <div className="order-7 relative">
                    <button
                      className="flex items-center text-gray-800 hover:text-[#ff9245] w-full"
                      onClick={handleSidebarNews}
                      type="button"
                    >
                      <svg
                        className="w-5 h-5 mr-3 text-[#ff9245]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      News &amp; Blogs
                    </button>
                    {showSidebarNews && (
                      <div className="ml-4 mt-4 space-y-2">
                        <Link
                          href="/blogs"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <LuNewspaper className="mr-3 text-[#ff9245] text-xl" />
                          Blogs
                        </Link>
                        <Link
                          href="/counselling"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <FaUserFriends className="mr-3 text-[#ff9245] text-xl" />
                          Counselling
                        </Link>
                        <Link
                          href="/collegeList"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <FaUniversity className="mr-3 text-[#ff9245] text-xl" />
                          Colleges
                        </Link>
                      </div>
                    )}
                  </div>

                  <Link
                    href="/services"
                    className="order-6 flex items-center text-gray-800 hover:text-[#ff9245]"
                    onClick={closeMenu}
                  >
                    <MdOutlineMiscellaneousServices className="mr-3 text-[#ff9245] text-xl" />
                    Services
                  </Link>
                  <div className="order-4 relative">
                    <button className="flex w-full items-center text-gray-800 hover:text-[#ff9245]" onClick={handleSidebarRank} type="button" aria-expanded={showSidebarRank}>
                      <LuStethoscope className="mr-3 text-xl text-[#ff9245]" />
                      Rank Predictor
                      {showRankNewBadge && <NewBadge />}
                      <svg className={`ml-2 h-4 w-4 transition-transform ${showSidebarRank ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" /></svg>
                    </button>
                    {showSidebarRank && (
                      <div className="ml-8 mt-3 space-y-2 border-l-2 border-orange-100 pl-4">
                        <Link href="/neet-rank-predictor" className="block text-gray-800 hover:text-[#ff9245]" onClick={closeMenu}>NEET UG</Link>
                        <Link href="/neet-pg-rank-predictor" className="block text-gray-800 hover:text-[#ff9245]" onClick={closeMenu}>NEET PG{showRankNewBadge && <NewBadge />}</Link>
                      </div>
                    )}
                  </div>
                  <Link
                    href="/neet-college-predictor"
                    className="order-5 flex items-center text-gray-800 hover:text-[#ff9245]"
                    onClick={closeMenu}
                  >
                    <LuUniversity className="mr-3 text-[#ff9245] text-xl" />
                    College Predictor
                  </Link>

                  {/* Webinar button + inline live animation */}
                  <div className="order-8 relative">
                    <button
                      className="flex items-center text-gray-800 hover:text-[#ff9245] w-full"
                      onClick={handleSidebarWebinarMore}
                      type="button"
                      aria-haspopup="true"
                      aria-expanded={showWebinarMore}
                    >
                      {/* Left arrow icon */}
                      <svg
                        className="w-5 h-5 mr-3 text-[#ff9245]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>

                      {/* Webinar label */}
                      <span>Webinar</span>

                      {/* Inline live animation */}
                      {isClient && (
                        <div className="ml-4 flex items-center">
                          <Player
                            autoplay
                            loop
                            src={Animation}
                            style={{ width: "40px", height: "17px" }}
                          />
                        </div>
                      )}
                    </button>

                    {/* Dropdown items */}
                    {showWebinarMore && (
                      <div className="ml-4 mt-4 space-y-2">
                        <Link
                          href="/ugwebinar"
                          className="flex items-center ml-8 text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          NEET UG
                        </Link>

                        <button
                          className="flex items-center ml-8 text-gray-800 hover:text-[#ff9245]"
                          onClick={() => {
                            window.open(
                              "https://livepgwebinar.neetsupport.com/",
                              "_blank"
                            );
                            closeMenu();
                          }}
                        >
                          NEET PG
                        </button>
                      </div>
                    )}
                  </div>

                  {/* More button and dropdown */}
                  <div className="order-9 relative">
                    <button
                      className="flex items-center text-gray-800 hover:text-[#ff9245] w-full"
                      onClick={handleSidebarMore}
                      type="button"
                    >
                      <svg
                        className="w-5 h-5 mr-3 text-[#ff9245]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      More
                    </button>
                    {showSidebarMore && (
                      <div className="ml-4 mt-4 space-y-2">
                        <Link
                          href="/About-Us"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <FaInfoCircle className="w-5 h-5 mr-2 text-[#ff9245] flex-shrink-0" />
                          About Us
                        </Link>

                        <Link
                          href="/contact"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <FaEnvelope className="w-5 h-5 mr-2 text-[#ff9245] flex-shrink-0" />
                          Contact
                        </Link>
                        <Link
                          href="/book-1-on-1-neet-counselling-session"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <FaUserFriends className="w-5 h-5 !mr-2 text-[#ff9245] flex-shrink-0" />
                          1:1 Session
                        </Link>
                        <Link
                          href="/seminar"
                          className="flex items-center text-gray-800 hover:text-[#ff9245]"
                          onClick={closeMenu}
                        >
                          <FaVideo className="w-5 h-5 mr-2 text-[#ff9245] flex-shrink-0" />
                          Seminar
                        </Link>
                      </div>
                    )}
                  </div>
                </nav>
                {/* Login / Signup Buttons OR Dashboard for Mobile */}
                <div className="flex space-x-4 text-sm login-signup xl:hidden ml-2 mt-8">
                  {isLoggedIn ? (
                    <Link
                      href="/dashboard"
                      className="px-16 py-2  rounded-full bg-[#ff9245] text-white border border-[#ff9245] 
                      hover:bg-white hover:!text-[#ff9245] transition-colors duration-300"
                      onClick={closeMenu}
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link
                        href="/login"
                        className="px-6 py-2 rounded-full border border-[#ff9245] text-[#ff9245] hover:bg-[#ff9245] hover:text-white transition-colors duration-300"
                        onClick={closeMenu}
                      >
                        Log In
                      </Link>
                      <Link
                        href="/signup"
                        className="px-6 py-2 rounded-full bg-[#ff9245] text-white border border-[#ff9245] 
                      hover:bg-white hover:!text-[#ff9245] transition-colors duration-300"
                        onClick={closeMenu}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <CartDrawer isOpen={isCartOpen} onClose={toggleCart} />{" "}
        {/* Add CartDrawer component */}
      </header>
      {showHeaderSearch && isSearchOpen && (
        <div
          className="fixed  top-[82px] inset-0 bg-gray-500 bg-opacity-75 z-50 flex justify-center items-center"
          onClick={handleCloseSearch}
        >
          <div
            className="absolute top-1 bg-white p-6 rounded-lg w-11/12 max-w-2xl"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
          >
            <button
              onClick={handleCloseSearch}
              className="absolute top-1 right-1  text-[#ff9245]"
            >
              <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
            </button>
            <input
              type="text"
              value={searchQuery}
              placeholder="Enter Keywords, Topics, Questions & more"
              className="w-full px-3 py-2  mt-4 rounded-full border border-[#ff9245] focus:outline-none focus:ring-1 focus:ring-[#ff9245] placeholder:text-xs"
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleSearch(e);
              }}
              autoFocus
            />
            {showResults && searchResults.length > 0 && (
              <ul className="mt-3 max-h-60 overflow-y-auto text-sm">
                {searchResults.map((post) => {
                  // Find the first matching tag in the mapping
                  const matchingTag = post.tags.find((tag) =>
                    Object.keys(tagIconMap).includes(tag.name.toLowerCase())
                  );

                  return (
                    <li
                      key={post.id}
                      className="flex items-center p-2 hover:bg-orange-100 cursor-pointer"
                      onClick={() => {
                        handleResultClick(post);
                        handleCloseSearch(); // Close modal after selecting a result
                      }}
                    >
                      {matchingTag &&
                        tagIconMap[matchingTag.name.toLowerCase()]}
                      <span>{post.title}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
